/** Ein Aufzählungszeichen oder eine Nummerierung beginnt eine eigene Zeile, nie eine Fortsetzung. */
const LIST_START = /^\s*([•▪◦*\-–—]|\d{1,2}[.)])\s+/;
/** Nur Protokoll-URLs. `mailto:` und alles andere bleibt Text — wir öffnen nichts, was wir nicht kennen. */
const URL_TOKEN = /https?:\/\/[^\s<>"']+/g;

/**
 * Zieht die Zeilenumbrüche einer für E-Mail gesetzten Nachricht auf Lesefluss zusammen.
 *
 * Die automatische Onboarding-Nachricht wird bei rund 72 Zeichen hart umbrochen — das ist eine
 * Entscheidung des Mailformats, keine des Absenders. Im Chat steht sie danach in schmalen
 * Bruchstücken, weil zum harten Umbruch noch der weiche der Blase kommt.
 *
 * Regel: **ein** Umbruch ist Transport und wird zum Leerzeichen, eine **Leerzeile** ist ein
 * Absatz und bleibt. Kein Wort ändert sich, nur die Stelle, an der die Zeile bricht — deshalb
 * braucht es auch keinen „Original anzeigen"-Umschalter.
 *
 * Konservativ bleibt die Funktion dort, wo der Umbruch erkennbar gewollt ist: vor einer
 * Aufzählung, vor und nach einer Zeile, die nur aus einer URL besteht, nach einem Doppelpunkt am
 * Zeilenende und an einer Trennlinie. Im Zweifel bleibt der Umbruch stehen.
 */
export function normalizeMessageBody(body: string): string {
  const paragraphs = body.replace(/\r\n?/g, '\n').split(/\n[ \t]*\n+/);
  return paragraphs
    .map((paragraph) => joinSoftBreaks(paragraph))
    .filter((paragraph) => paragraph.length > 0)
    .join('\n\n');
}

function joinSoftBreaks(paragraph: string): string {
  const lines = paragraph.split('\n').map((line) => line.trim());
  let out = '';
  for (const [index, line] of lines.entries()) {
    if (index === 0) {
      out = line;
      continue;
    }
    out += keepsBreakBefore(lines[index - 1]!, line) ? '\n' : ' ';
    out += line;
  }
  return out.trim();
}

function keepsBreakBefore(previous: string, line: string): boolean {
  if (previous.length === 0 || line.length === 0) return true;
  // Bewusst nur die **neue** Zeile: steht dort ein Aufzählungszeichen, beginnt ein eigener Punkt.
  // Auf die vorige zu schauen wäre falsch — die Fortsetzungszeile eines langen Punkts steht
  // hinter einem Aufzählungszeichen und gehört trotzdem an ihren Punkt angehängt.
  if (LIST_START.test(line)) return true;
  if (isOnlyUrl(line) || isOnlyUrl(previous)) return true;
  if (isRule(line) || isRule(previous)) return true;
  return previous.endsWith(':');
}

function isOnlyUrl(line: string): boolean {
  return /^https?:\/\/\S+$/.test(line);
}

function isRule(line: string): boolean {
  return /^[-–—_=*]{3,}$/.test(line);
}

export interface MessageSegment {
  type: 'text' | 'link';
  value: string;
}

/**
 * Zerlegt einen Text in Fließtext- und Link-Abschnitte.
 *
 * Bewusst eine Zerlegung und **keine** HTML-Erzeugung: `dangerouslySetInnerHTML` mit fremdem
 * Nachrichteninhalt wäre eine Injektionsfläche für jeden, der uns über einen Kanal schreiben
 * darf. Die Abschnitte werden von React als Text bzw. als `<a>` gerendert.
 */
export function splitMessageLinks(text: string): MessageSegment[] {
  const segments: MessageSegment[] = [];
  let cursor = 0;
  for (const match of text.matchAll(URL_TOKEN)) {
    const start = match.index;
    // Satzzeichen am Ende gehören zum Satz, nicht zur Adresse — „… unter http://x/y." endet nicht
    // auf einen Punkt im Link.
    const url = match[0].replace(/[.,;:!?)\]}»"']+$/, '');
    if (start > cursor) segments.push({ type: 'text', value: text.slice(cursor, start) });
    segments.push({ type: 'link', value: url });
    cursor = start + url.length;
  }
  if (cursor < text.length) segments.push({ type: 'text', value: text.slice(cursor) });
  return segments;
}

/**
 * Erkennt einen Gast-Magic-Link — eine Adresse, die selbst der Zugang ist.
 *
 * Ein solcher Link ist kein Verweis, sondern ein Berechtigungsnachweis, und sein Abruf ist ein
 * **schreibender** Vorgang: `MagicLinkService.Validate` zählt `UseCount` hoch, setzt `FirstUsedAt`
 * und kappt bei `MaxUseCount` endgültig. Und `UseCount > 0` ist im ganzen System die Definition
 * von „der Gast hat den Link geöffnet": die Kennzahl `OnboardingLinksOpen` des Dashboards zählt
 * nur `UseCount == 0`, die Onboarding-Linkliste und die Support-Sicht zeigen dann „Verwendet".
 * Ein neugieriger Klick aus der Inbox würde also die Auskunft über den Gast verfälschen und ihm
 * eine seiner Verwendungen wegnehmen.
 */
export function isGuestCredentialLink(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.pathname.includes('/onboarding') && parsed.searchParams.has('token');
  } catch {
    return false;
  }
}

/**
 * Kürzt eine Adresse auf das, was ein Mensch daran wiedererkennt: Host und Pfadanfang.
 *
 * Der Magic-Link-Token ist 43 Zeichen lang und sagt niemandem etwas; vollständig steht die
 * Adresse weiterhin im `href` und im Titel des Links. Gekürzt wird die **Darstellung**, nicht
 * das Ziel.
 */
export function shortenUrl(url: string, maxLength = 42): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  const host = parsed.host;
  const firstSegment = parsed.pathname.split('/').filter(Boolean)[0];
  const visible = firstSegment ? `${host}/${firstSegment}` : host;
  const hasMore =
    parsed.pathname.replace(/\/+$/, '').split('/').filter(Boolean).length > 1 ||
    parsed.search.length > 0 ||
    parsed.hash.length > 0;
  const shortened = hasMore ? `${visible}/…` : visible;
  return shortened.length > maxLength ? `${shortened.slice(0, maxLength - 1)}…` : shortened;
}
